import { useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import Header from "@/components/feature/Header";
import Footer from "@/components/feature/Footer";
import { mainSiteUrl } from "@/mocks/site";
import { getArticleBySlug } from "@/mocks/articles";
import { ArticleJsonLd, FaqJsonLd } from "@/components/feature/SeoJsonLd";

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = slug ? getArticleBySlug(slug) : undefined;

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [slug]);

  if (!article) {
    return (
      <div className="flex min-h-screen flex-col bg-background-50">
        <Header />
        <main className="flex flex-1 items-center justify-center px-4 py-24 text-center">
          <div>
            <h1 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
              Article not found
            </h1>
            <p className="mt-3 text-foreground-600">
              The page you are looking for does not exist or has been moved.
            </p>
            <Link
              to="/"
              className="mt-6 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-primary-600"
            >
              Back to Homepage
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col bg-background-50">
      <ArticleJsonLd article={article} />
      <FaqJsonLd faqs={article.faqs} />
      <Header />
      <main className="flex-1">
        <article>
          {/* Article header */}
          <header className="w-full bg-primary-50 py-12 md:py-16">
            <div className="mx-auto w-full max-w-3xl px-4 md:px-6">
              <nav aria-label="Breadcrumb" className="text-sm text-foreground-500">
                <Link to="/" className="font-medium transition-colors hover:text-primary-700">
                  Home
                </Link>
                <span className="mx-2">/</span>
                <span className="text-foreground-700">{article.category}</span>
              </nav>
              <h1 className="mt-5 font-heading text-2xl font-bold leading-tight tracking-tight text-foreground-950 md:text-4xl">
                {article.title}
              </h1>
              <div className="mt-5 flex flex-wrap items-center gap-4 text-sm font-medium text-foreground-500">
                <span className="inline-flex items-center gap-1.5 rounded-full bg-white px-3 py-1 text-primary-700">
                  <i className="ri-price-tag-3-line" aria-hidden="true"></i>
                  {article.category}
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-calendar-line" aria-hidden="true"></i>
                  Updated August 2026
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-time-line" aria-hidden="true"></i>
                  {article.readTime}
                </span>
              </div>
            </div>
          </header>

          {/* Article body */}
          <div className="mx-auto w-full max-w-3xl px-4 py-12 md:px-6">
            <div className="prose-sm max-w-none space-y-8 text-base leading-relaxed text-foreground-700">
              <p className="text-lg font-medium text-foreground-900">{article.intro}</p>

              {article.sections.map((section) => (
                <section key={section.heading}>
                  <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
                    {section.heading}
                  </h2>

                  {section.paragraphs?.map((p) => (
                    <p key={p.slice(0, 40)}>{p}</p>
                  ))}

                  {section.steps && (
                    <ol className="space-y-3">
                      {section.steps.map((step, i) => (
                        <li key={step} className="flex gap-3">
                          <span className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-primary-100 text-sm font-bold text-primary-700">
                            {i + 1}
                          </span>
                          <span>{step}</span>
                        </li>
                      ))}
                    </ol>
                  )}

                  {section.list && (
                    <ul className="space-y-2.5">
                      {section.list.map((item) => (
                        <li key={item} className="flex items-start gap-3">
                          <i
                            className="ri-checkbox-circle-line mt-0.5 text-primary-600"
                            aria-hidden="true"
                          ></i>
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </section>
              ))}

              <section>
                <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
                  Frequently Asked Questions
                </h2>
                <div className="space-y-4">
                  {article.faqs.map((faq) => (
                    <div
                      key={faq.q}
                      className="rounded-lg border border-background-200 bg-white p-5"
                    >
                      <h3 className="font-heading text-base font-semibold text-foreground-950">
                        {faq.q}
                      </h3>
                      <p className="mt-2 text-sm text-foreground-600">{faq.a}</p>
                    </div>
                  ))}
                </div>
              </section>
            </div>

            {/* Related links */}
            <div className="mt-12 rounded-lg border border-background-200 bg-background-100 p-6">
              <p className="text-xs font-bold uppercase tracking-widest text-foreground-500">
                Continue Reading
              </p>
              <p className="mt-2 text-sm text-foreground-700">
                Want more Basetao guidance? Head back to the homepage to explore all our guides and
                browse every product category.
              </p>
              <Link
                to="/"
                className="mt-4 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-primary-600"
              >
                <i className="ri-home-4-line text-base" aria-hidden="true"></i>
                Return to Homepage
              </Link>
            </div>

            {/* CTA */}
            <div className="mt-8 rounded-lg bg-primary-500 p-8 text-center">
              <h2 className="font-heading text-xl font-bold text-white md:text-2xl">
                Ready to Start Your Research?
              </h2>
              <p className="mx-auto mt-3 max-w-xl text-sm leading-relaxed text-primary-50">
                Explore the full Basetao Spreadsheet and continue your product research on the main
                website.
              </p>
              <div className="mt-6 flex flex-col items-center justify-center gap-3 sm:flex-row">
                <a
                  href={mainSiteUrl}
                  target="_blank"
                  rel="noopener nofollow"
                  className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-white px-6 py-3 text-sm font-semibold text-primary-700 transition-colors hover:bg-background-100"
                >
                  Explore Basetao Spreadsheet
                  <i className="ri-arrow-right-up-line text-base" aria-hidden="true"></i>
                </a>
                <Link
                  to="/"
                  className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md border border-white/40 px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-primary-600"
                >
                  Back to Homepage
                </Link>
              </div>
            </div>
          </div>
        </article>
      </main>
      <Footer />
    </div>
  );
}